import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './NestModal.css';

/**
 * NestModal Component
 * 
 * Modal that appears when dragging a node onto another node,
 * offering options for how to handle dependencies.
 */
const NestModal = ({
  isOpen,
  targetTask,
  draggedIds = [],
  dependencyInfo,
  hasDependencies,
  tasks = [],
  onConfirm,
  onCancel,
}) => {
  const [selectedStrategy, setSelectedStrategy] = useState('patch');
  const [rememberChoice, setRememberChoice] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStrategy(hasDependencies ? 'patch' : 'break');
    }
  }, [isOpen, hasDependencies]);

  // Handle keyboard
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
      } else if (e.key === 'Enter') {
        handleConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, selectedStrategy]);

  const handleConfirm = useCallback(() => {
    onConfirm?.(selectedStrategy, rememberChoice);
  }, [onConfirm, selectedStrategy, rememberChoice]);

  if (!isOpen || !targetTask) return null;

  // Get task names for display
  const draggedTaskNames = draggedIds
    .map(id => tasks.find(t => t.id === id)?.name || 'Unknown')
    .slice(0, 3);
  
  const moreCount = draggedIds.length - 3;
  const draggedLabel = draggedIds.length === 1 
    ? `"${draggedTaskNames[0]}"` 
    : `${draggedIds.length} tasks`;

  // Get upstream/downstream task names
  const upstreamNames = Array.from(dependencyInfo?.upstreamIds || [])
    .map(id => tasks.find(t => t.id === id)?.name || 'Unknown')
    .slice(0, 2);
  
  const downstreamNames = Array.from(dependencyInfo?.downstreamIds || [])
    .map(id => tasks.find(t => t.id === id)?.name || 'Unknown')
    .slice(0, 2);

  const totalChainCount = draggedIds.length + 
    (dependencyInfo?.upstreamCount || 0) + 
    (dependencyInfo?.downstreamCount || 0);

  return createPortal(
    <div className="nest-modal__overlay" onClick={onCancel}>
      <div className="nest-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="nest-modal__header">
          <div className="nest-modal__icon">📦</div>
          <div className="nest-modal__title">
            Move {draggedLabel} into "{targetTask.name}"?
          </div>
        </div>

        {/* Dependency info */}
        {hasDependencies && (
          <div className="nest-modal__info">
            <div className="nest-modal__info-label">This task has connections:</div>
            <ul className="nest-modal__info-list">
              {dependencyInfo.upstreamCount > 0 && (
                <li>
                  <span className="nest-modal__info-count">{dependencyInfo.upstreamCount}</span>
                  {' upstream '}
                  {dependencyInfo.upstreamCount === 1 ? 'dependency' : 'dependencies'}
                  {upstreamNames.length > 0 && (
                    <span className="nest-modal__info-names">
                      ({upstreamNames.join(', ')}{dependencyInfo.upstreamCount > 2 ? '...' : ''})
                    </span>
                  )}
                </li>
              )}
              {dependencyInfo.downstreamCount > 0 && (
                <li>
                  <span className="nest-modal__info-count">{dependencyInfo.downstreamCount}</span>
                  {' downstream '}
                  {dependencyInfo.downstreamCount === 1 ? 'dependent' : 'dependents'}
                  {downstreamNames.length > 0 && (
                    <span className="nest-modal__info-names">
                      ({downstreamNames.join(', ')}{dependencyInfo.downstreamCount > 2 ? '...' : ''})
                    </span>
                  )}
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Options */}
        <div className="nest-modal__options">
          {/* Option 1: Break connections */}
          <label 
            className={`nest-modal__option ${selectedStrategy === 'break' ? 'nest-modal__option--selected' : ''}`}
            onClick={() => setSelectedStrategy('break')}
          >
            <input
              type="radio"
              name="nestStrategy"
              value="break"
              checked={selectedStrategy === 'break'}
              onChange={() => setSelectedStrategy('break')}
            />
            <div className="nest-modal__option-content">
              <div className="nest-modal__option-title">
                <span className="nest-modal__option-icon">✂️</span>
                Move only — Remove all connections
              </div>
              <div className="nest-modal__option-desc">
                {draggedIds.length === 1 ? 'Task' : 'Tasks'} will have no dependencies in new location
              </div>
            </div>
          </label>

          {/* Option 2: Patch connections (only if has dependencies) */}
          {hasDependencies && (
            <label 
              className={`nest-modal__option ${selectedStrategy === 'patch' ? 'nest-modal__option--selected' : ''}`}
              onClick={() => setSelectedStrategy('patch')}
            >
              <input
                type="radio"
                name="nestStrategy"
                value="patch"
                checked={selectedStrategy === 'patch'}
                onChange={() => setSelectedStrategy('patch')}
              />
              <div className="nest-modal__option-content">
                <div className="nest-modal__option-title">
                  <span className="nest-modal__option-icon">🔗</span>
                  Move only — Patch connections
                </div>
                <div className="nest-modal__option-desc">
                  Upstream tasks will connect directly to downstream tasks
                </div>
              </div>
            </label>
          )}

          {/* Option 3: Move entire chain (only if has dependencies) */}
          {hasDependencies && (
            <label 
              className={`nest-modal__option ${selectedStrategy === 'chain' ? 'nest-modal__option--selected' : ''}`}
              onClick={() => setSelectedStrategy('chain')}
            >
              <input
                type="radio"
                name="nestStrategy"
                value="chain"
                checked={selectedStrategy === 'chain'}
                onChange={() => setSelectedStrategy('chain')}
              />
              <div className="nest-modal__option-content">
                <div className="nest-modal__option-title">
                  <span className="nest-modal__option-icon">🔀</span>
                  Move entire chain ({totalChainCount} tasks)
                </div>
                <div className="nest-modal__option-desc">
                  All connected tasks will move together into the nested campaign
                </div>
              </div>
            </label>
          )}
        </div>

        {/* Remember choice */}
        <label className="nest-modal__remember">
          <input
            type="checkbox"
            checked={rememberChoice}
            onChange={(e) => setRememberChoice(e.target.checked)}
          />
          <span>Remember my choice</span>
        </label>

        {/* Actions */}
        <div className="nest-modal__actions">
          <button 
            className="nest-modal__btn nest-modal__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            className="nest-modal__btn nest-modal__btn--confirm"
            onClick={handleConfirm}
          >
            Move
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NestModal;

